import {
  CurrencyCurrency,
  type ActivityForm,
  type ActivityPlanUnitItem,
  type Booking,
  type Form,
  type Product,
  type SessionSession
} from '@linktivity/ars-neo';
import { AGENT_ID } from '@link/share/constants';
// Relative rather than `@link/share/utils`: a vendor module has no business
// pulling the whole utils barrel — and the business components it re-exports —
// into every app's entry chunk.
import { roundToCurrency } from '../../utils/price';
import { resolveParticipationDate } from '../../utils/validity';
import { buildItemVariant, normalizeCoupon } from './format';
import { distributeDiscount, type DiscountLine } from './money';
import { getGa4SiteAgentId } from './site';
import {
  GA4_ITEM_CATEGORY,
  type Ga4Ecommerce,
  type Ga4Item,
  type Ga4ItemCategory
} from './types';

/** One ecommerce event's data: the agent it reports and its `ecommerce` block. */
export interface Ga4EcommerceData {
  agentId: string;
  ecommerce: Ga4Ecommerce;
}

export interface SessionItemFallback {
  agentId?: string;
  activityId?: string;
  activityTitle?: string;
}

export interface Ga4TrackOptions {
  /**
   * Fallback currency from the current site context. Order/session currency is
   * authoritative whenever the payload supplies it.
   */
  currency?: CurrencyCurrency;
  /**
   * True when the site shows converted prices, i.e. the `display*` amounts are
   * the ones on screen. Selects the amount basis for every monetary field.
   */
  showOtherCurrencies: boolean;
  /** Fills in item identity the API may not echo back. */
  fallback?: SessionItemFallback;
  /** See `PushGa4EventParams.uiLanguage`. */
  uiLanguage?: string;
}

/** Order-level money, in both the settlement and the converted currency. */
export interface Ga4OrderTotals {
  currency?: CurrencyCurrency;
  displayCurrency?: CurrencyCurrency;
  amount?: number | null;
  displayAmount?: number | null;
  finalAmount?: number | null;
  displayFinalAmount?: number | null;
  discountAmount?: number | null;
  displayDiscountAmount?: number | null;
}

/** A checkout: one session, or every session of a cart. */
export interface Ga4Checkout {
  /** De-dupe key: the session id, or the cart id for a multi-item checkout. */
  id: string;
  sessions: SessionSession[];
  /** Order-level totals. Omitted for a single session, which carries its own. */
  totals?: Ga4OrderTotals;
  /** Order-level coupons. Omitted to read them off the single session. */
  couponCodes?: string[];
}

interface OrderForms {
  mainForm?: Form;
  optionForms: Form[];
}

/**
 * Which of the two amount sets is reported, and under which code. Resolved once
 * per event so the total, the per-unit prices and the discount can never be
 * mixed across the display/settlement boundary.
 */
interface MoneyBasis {
  /**
   * The reported ISO code. Undefined when none could be resolved, in which case
   * no monetary field is reported at all — GA4 discards a value with no
   * currency, and a value under the wrong currency is worse than none.
   */
  currency?: string;
  display: boolean;
}

interface ItemDraft {
  item: Ga4Item;
  line: DiscountLine;
}

/** Drop UNKNOWN, the proto zero value, so no bogus ISO code is reported. */
const normalizeCurrency = (
  currency: CurrencyCurrency | undefined
): string | undefined =>
  !currency || currency === CurrencyCurrency.Unknown ? undefined : currency;

const pick = (
  base: number | null | undefined,
  display: number | null | undefined,
  useDisplay: boolean
): number | undefined => (useDisplay ? display : base) ?? undefined;

function resolveBasis(
  totals: Ga4OrderTotals,
  options: Ga4TrackOptions
): MoneyBasis {
  // Report the converted amounts only when the order actually carries them.
  // An unconverted payload means the site currency is the settlement currency,
  // which is what the price UI falls back to as well.
  const display =
    options.showOtherCurrencies &&
    (totals.displayFinalAmount != null || totals.displayAmount != null);
  // Normalized per candidate, so a literal UNKNOWN cannot beat a valid code.
  const currency =
    normalizeCurrency(display ? totals.displayCurrency : totals.currency) ??
    normalizeCurrency(options.currency);
  return { currency, display };
}

/**
 * The order-level discount to apportion across the lines, in the reported
 * currency. Often reported only as the gap between the two order totals.
 */
function resolveOrderDiscount(
  totals: Ga4OrderTotals,
  basis: MoneyBasis
): number {
  const reported = pick(
    totals.discountAmount,
    totals.displayDiscountAmount,
    basis.display
  );
  if (reported !== undefined) return reported;

  const gross = pick(totals.amount, totals.displayAmount, basis.display);
  const net = pick(
    totals.finalAmount,
    totals.displayFinalAmount,
    basis.display
  );
  return gross !== undefined && net !== undefined
    ? Math.max(0, gross - net)
    : 0;
}

/**
 * A unit's gross in the reported currency, or undefined when it has none. Units
 * carry their own currency, so a unit priced in something else is left unpriced
 * rather than reported as if it matched the order.
 */
function resolveUnitGross(
  unit: ActivityPlanUnitItem,
  quantity: number,
  basis: MoneyBasis
): number | undefined {
  const unitCurrency = normalizeCurrency(
    basis.display ? unit.displayCurrency : unit.currency
  );
  if (unitCurrency && basis.currency && unitCurrency !== basis.currency) {
    return undefined;
  }
  const amount = pick(unit.amount, unit.displayAmount, basis.display);
  return amount === undefined ? undefined : amount * quantity;
}

/** One draft per priced unit. Units with no quantity are not sold. */
function formToDrafts({
  activityForm,
  itemId,
  itemName,
  affiliation,
  category,
  parentItemId,
  basis
}: {
  activityForm?: ActivityForm;
  itemId: string;
  itemName: string;
  affiliation: string;
  category: Ga4ItemCategory;
  parentItemId?: string;
  basis: MoneyBasis;
}): ItemDraft[] {
  // Only a date the user actually participates on is a visit date. No plan is
  // in reach here, so a form written before `participationDate` existed reports
  // none rather than the date an open-date plan had picked for it.
  const visitDate = resolveParticipationDate(activityForm) || undefined;

  return (activityForm?.planUnitItems ?? [])
    .filter(unit => (unit.count ?? 0) > 0)
    .map(unit => {
      const quantity = unit.count ?? 0;
      const variant = buildItemVariant(activityForm?.planTitle, unit.title);

      return {
        item: {
          item_id: itemId,
          item_name: itemName,
          affiliation,
          item_category: category,
          ...(variant ? { item_variant: variant } : {}),
          quantity,
          ...(parentItemId ? { parent_item_id: parentItemId } : {}),
          ...(visitDate ? { visit_date: visitDate } : {})
        },
        line: { gross: resolveUnitGross(unit, quantity, basis), quantity }
      };
    });
}

function buildEcommerce({
  orders,
  affiliation,
  totals,
  couponCodes,
  options
}: {
  orders: OrderForms[];
  affiliation: string;
  totals: Ga4OrderTotals;
  couponCodes?: string[];
  options: Ga4TrackOptions;
}): Ga4EcommerceData {
  const basis = resolveBasis(totals, options);
  const fallback = options.fallback ?? {};
  const echoedAgentId = affiliation || fallback.agentId;
  // One affiliation rule for every entry point. AGENT_ID is this site's own
  // agent, read off the hostname, so the field is never empty even when the API
  // does not echo one back.
  const resolvedAffiliation = echoedAgentId || AGENT_ID;

  const drafts = orders.flatMap(order => {
    const mainActivityForm = order.mainForm?.activityForm;
    const mainItemId =
      mainActivityForm?.activityId || fallback.activityId || '';

    return [
      ...formToDrafts({
        activityForm: mainActivityForm,
        itemId: mainItemId,
        itemName:
          mainActivityForm?.activityTitle || fallback.activityTitle || '',
        affiliation: resolvedAffiliation,
        category: GA4_ITEM_CATEGORY.MAIN,
        basis
      }),
      // Options hang off the main product of their own session, never another's.
      ...order.optionForms.flatMap(optionForm =>
        formToDrafts({
          activityForm: optionForm.activityForm,
          itemId: optionForm.activityForm?.activityId ?? '',
          itemName: optionForm.activityForm?.activityTitle ?? '',
          affiliation: resolvedAffiliation,
          category: GA4_ITEM_CATEGORY.OPTION,
          parentItemId: mainItemId,
          basis
        })
      )
    ];
  });

  const distributed = distributeDiscount(
    drafts.map(draft => draft.line),
    resolveOrderDiscount(totals, basis),
    basis.currency
  );

  const items = drafts.map((draft, index) => ({
    ...draft.item,
    ...(basis.currency ? distributed[index] : {})
  }));

  // GA4 defines value as the sum of the item lines, so it always reconciles with
  // `items`; the order totals only decide how the discount is apportioned, and a
  // fee that is not an item of its own is deliberately left out.
  const value = items.some(item => item.price !== undefined)
    ? roundToCurrency(
        items.reduce(
          (sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 0),
          0
        ),
        basis.currency
      )
    : undefined;
  const coupon = normalizeCoupon(couponCodes);

  return {
    agentId: getGa4SiteAgentId() || echoedAgentId || '',
    ecommerce: {
      ...(basis.currency && value !== undefined
        ? { currency: basis.currency, value }
        : {}),
      ...(coupon ? { coupon } : {}),
      items
    }
  };
}

/** Source for add_to_cart, begin_checkout and payment_redirect. */
export function checkoutToGa4Ecommerce(
  checkout: Ga4Checkout,
  options: Ga4TrackOptions
): Ga4EcommerceData {
  const [first] = checkout.sessions;

  return buildEcommerce({
    orders: checkout.sessions.map(session => ({
      mainForm: session.form,
      optionForms: session.optionForms ?? []
    })),
    affiliation: first?.agentId ?? '',
    totals: checkout.totals ?? first ?? {},
    couponCodes: checkout.couponCodes ?? first?.couponCodes,
    options
  });
}

/** Source for purchase. Uses the final order, never the pre-payment session. */
export function bookingToGa4Ecommerce(
  booking: Booking,
  options: Ga4TrackOptions
): Ga4EcommerceData {
  const confirmation = booking.bookingConfirmation;

  return buildEcommerce({
    orders: [
      {
        mainForm: confirmation?.externalBooking?.form,
        optionForms: (confirmation?.optionExternalBookings ?? [])
          .map(externalBooking => externalBooking.form)
          .filter((form): form is Form => Boolean(form))
      }
    ],
    affiliation: booking.agentId ?? '',
    totals: confirmation ?? {},
    couponCodes: confirmation?.couponCodes,
    options
  });
}

/**
 * Source for view_item: the main product body and nothing else. No plan, unit,
 * variant, quantity, date, currency or amount — the user is looking at the
 * product and has not chosen what to buy, so there is no money to report.
 */
export function productToGa4Ecommerce(product: Product): Ga4EcommerceData {
  const activity = product.productContent?.activity;

  return {
    agentId: getGa4SiteAgentId() || product.agentId || '',
    ecommerce: {
      items: [
        {
          item_id: activity?.id ?? '',
          item_name: activity?.title ?? '',
          affiliation: product.agentId || AGENT_ID,
          item_category: GA4_ITEM_CATEGORY.MAIN
        }
      ]
    }
  };
}
